import React, { useContext, useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green, blue, red, grey } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";
import { head } from "lodash";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import MessageVariablesPicker from "../MessageVariablesPicker";
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  Box,
  Avatar,
  Typography,
  Divider
} from "@material-ui/core";
import ConfirmationModal from "../ConfirmationModal";
import Picker from '@emoji-mart/react';
import loadEmojiData from "../../utils/loadEmojiData";
import Paper from '@material-ui/core/Paper';
import Draggable from 'react-draggable';
import { Slide } from '@material-ui/core';
import {
  ShortText as ShortTextIcon,
  Message as MessageIcon,
  Visibility as VisibilityIcon,
  Public as PublicIcon,
  InsertEmoticon as EmojiIcon,
  Code as CodeIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Headset as AudioIcon,
  Description as DocumentIcon,
  AddCircleOutline as AddCircleIcon,
  Link as LinkIcon,
  FileCopy as CopyIcon
} from '@material-ui/icons';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import MicRecorder from "mic-recorder-to-mp3";
import MicIcon from "@material-ui/icons/Mic";

const path = require('path');

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
  dialogTitle: {
    backgroundColor: "#3f51b5",
    color: "#fff",
    padding: theme.spacing(2),
    textAlign: "left",
    cursor: 'move',
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5),
    },
  },
  dialogContent: {
    padding: theme.spacing(3),
    background: '#fff',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 200px)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      maxHeight: 'calc(100vh - 180px)',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5),
      maxHeight: 'calc(100vh - 160px)',
    },
  },
  dialogActions: {
    padding: theme.spacing(2),
    justifyContent: "space-between",
    background: '#fff',
    borderBottomLeftRadius: "8px",
    borderBottomRightRadius: "8px",
    borderTop: `1px solid ${grey[200]}`,
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      gap: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5),
    },
  },
  textField: {
    marginBottom: theme.spacing(3),
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      background: '#fff',
      boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
      "&.Mui-focused fieldset": {
        borderColor: blue[400],
        boxShadow: '0 0 0 2px rgba(102, 126, 234, 0.2)',
      },
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color: blue[400],
    },
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
  buttonCancel: {
    background: `linear-gradient(135deg, ${red[500]} 0%, ${red[700]} 100%)`,
    color: "white",
    borderRadius: "8px",
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    padding: '8px 20px',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.5px',
    transition: 'all 0.3s ease',
    "&:hover": {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 10px rgba(0,0,0,0.15)',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.down('xs')]: {
      padding: '6px 16px',
      fontSize: '0.875rem',
    },
  },
  buttonSave: {
    background: `linear-gradient(135deg, ${green[500]} 0%, ${green[700]} 100%)`,
    color: "white",
    borderRadius: "8px",
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    padding: '8px 20px',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.5px',
    transition: 'all 0.3s ease',
    "&:hover": {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 10px rgba(0,0,0,0.15)',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.down('xs')]: {
      padding: '6px 16px',
      fontSize: '0.875rem',
    },
  },
  buttonAttach: {
    background: `linear-gradient(135deg, ${blue[400]} 0%, ${blue[600]} 100%)`,
    color: "white",
    borderRadius: "8px",
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    padding: '8px 20px',
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: '0.5px',
    transition: 'all 0.3s ease',
    "&:hover": {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 10px rgba(0,0,0,0.15)',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      padding: '6px 16px',
      fontSize: '0.875rem',
    },
  },
  emojiPickerContainer: {
    position: "relative",
    display: "inline-block",
  },
  emojiPicker: {
    position: "absolute",
    zIndex: 1000,
    top: "100%",
    right: 0,
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      left: 0,
      right: 'auto',
    },
  },
  emojiButton: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing(1),
    borderRadius: '50%',
    transition: 'background-color 0.3s',
    "&:hover": {
      backgroundColor: grey[200],
    },
    "&:focus": {
      outline: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.5),
    }
  },
  paper: {
    borderRadius: "12px !important",
    overflow: "hidden",
    minWidth: '700px',
    maxWidth: '800px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      minWidth: '90%',
      maxWidth: '90%',
      margin: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: '95%',
      maxWidth: '95%',
      margin: theme.spacing(0.5),
      maxHeight: '95vh',
    },
  },
  headerIcon: {
    background: 'rgba(255,255,255,0.2)',
    padding: theme.spacing(1),
    borderRadius: '50%',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      marginRight: theme.spacing(1),
      padding: theme.spacing(0.75),
      width: 32,
      height: 32,
    },
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    color: grey[800],
    fontWeight: 600,
    fontSize: '1rem',
    "& svg": {
      marginRight: theme.spacing(1.5),
      color: blue[500],
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.85rem',
      marginBottom: theme.spacing(1.5),
    }
  },
  attachmentBox: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    background: '#fff',
    borderRadius: '10px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    marginBottom: theme.spacing(3),
    border: `1px solid ${grey[200]}`,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
    },
  },
  attachmentIcon: {
    background: blue[50],
    color: blue[500],
    marginRight: theme.spacing(2),
    width: 40,
    height: 40,
    [theme.breakpoints.down('xs')]: {
      width: 32,
      height: 32,
      marginRight: theme.spacing(1.5),
    },
  },
  variableButton: {
    background: grey[100],
    color: grey[800],
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 500,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'all 0.2s',
    "&:hover": {
      background: grey[200],
    },
    "& svg": {
      marginRight: '6px',
      fontSize: '16px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px',
      padding: '4px 8px',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '11px',
      padding: '3px 6px',
      marginRight: theme.spacing(0.75),
      marginBottom: theme.spacing(0.75),
    }
  },
  divider: {
    margin: theme.spacing(3, 0),
    background: grey[200],
    height: 2,
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2, 0),
    },
  },
  inputIcon: {
    color: grey[500],
    [theme.breakpoints.down('xs')]: {
      fontSize: '1rem',
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  actionButtonsContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexDirection: 'column',
    },
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1),
    },
  },
  formGridContainer: {
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  messageInputContainer: {
    position: 'relative',
  },
  messageCounter: {
    position: 'absolute',
    right: theme.spacing(1),
    bottom: theme.spacing(-3),
    color: grey[500],
    fontSize: '0.75rem',
    background: 'transparent',
    padding: theme.spacing(0.25, 1),
    borderRadius: '4px',
    [theme.breakpoints.down('xs')]: {
      bottom: theme.spacing(-2.5),
      fontSize: '0.7rem',
    },
  },
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(2),
    },
  },
  mobileToolbar: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  mobileActionButtons: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  scrollableContent: {
    overflowY: 'auto',
    flex: 1,
  },
  stickyFooter: {
    position: 'sticky',
    bottom: 0,
    background: '#fff',
    zIndex: 1,
    borderTop: `1px solid ${grey[200]}`,
  },
}));

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} style={{ borderRadius: "12px", overflow: "hidden" }} />
    </Draggable>
  );
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const QuickMessageSchema = Yup.object().shape({
  shortcode: Yup.string().required("Obrigatório"),
});

// Mesmo gravador do chat: a resposta rapida pode ser uma mensagem de voz
// gravada na hora, sem precisar de arquivo pronto.
const GravadorMp3 = new MicRecorder({ bitRate: 128 });

const QuickMessageDialog = ({ open, onClose, quickemessageId, reload }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const messageInputRef = useRef();
  const emojiPickerRef = useRef();
  const emojiButtonRef = useRef();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiData, setEmojiData] = useState(null);

  const initialState = {
    shortcode: "",
    message: "",
    geral: false,
    status: true,
    messageType: "text",
    buttons: [],
  };

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [quickemessage, setQuickemessage] = useState(initialState);
  const [attachment, setAttachment] = useState(null);
  const [gravando, setGravando] = useState(false);
  const [tempoGravado, setTempoGravado] = useState(0);
  const relogioGravacao = useRef(null);

  const iniciarGravacao = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await GravadorMp3.start();
      setGravando(true);
      setTempoGravado(0);
      relogioGravacao.current = setInterval(
        () => setTempoGravado(anterior => anterior + 1),
        1000
      );
    } catch (err) {
      setGravando(false);
      toast.error(
        err?.name === "NotAllowedError"
          ? "Microfone bloqueado. Clique no cadeado ao lado do endereço e permita o uso do microfone."
          : "Não consegui acessar o microfone."
      );
    }
  };

  const pararGravacao = async () => {
    try {
      const [, blob] = await GravadorMp3.stop().getMp3();
      if (relogioGravacao.current) {
        clearInterval(relogioGravacao.current);
        relogioGravacao.current = null;
      }
      setGravando(false);
      if (!blob || blob.size < 8000) {
        toast.error("Gravação muito curta. Grave por mais alguns segundos.");
        return;
      }
      const nome = `resposta-${new Date().getTime()}.mp3`;
      setAttachment(new File([blob], nome, { type: "audio/mpeg" }));
      toast.success("Áudio gravado e anexado à resposta rápida.");
    } catch (err) {
      setGravando(false);
      toast.error("Não consegui finalizar a gravação.");
    }
  };
  const attachmentFile = useRef(null);

  useEffect(() => {
    let mounted = true;
    loadEmojiData()
      .then((dataset) => {
        if (mounted) {
          setEmojiData(dataset);
        }
      })
      .catch(() => {
        if (mounted) {
          setEmojiData(null);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    try {
      (async () => {
        if (!quickemessageId) return;

        const { data } = await api.get(`/quick-messages/${quickemessageId}`);

        setQuickemessage((prevState) => {
          return { ...prevState, ...data };
        });
      })();
    } catch (err) {
      toastError(err);
    }
  }, [quickemessageId, open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && 
          !emojiPickerRef.current.contains(event.target) &&
          !emojiButtonRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClose = () => {
    setQuickemessage(initialState);
    setAttachment(null);
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveQuickMessage = async (values) => {
    const quickemessageData = {
      ...values,
      isMedia: true,
      mediaPath: attachment
        ? String(attachment.name).replace(/ /g, "_")
        : values.mediaPath
        ? path.basename(values.mediaPath).replace(/ /g, "_")
        : null,
    };

    try {
      if (quickemessageId) {
        await api.put(`/quick-messages/${quickemessageId}`, quickemessageData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "quickMessage");
          formData.append("file", attachment);
          await api.post(
            `/quick-messages/${quickemessageId}/media-upload`,
            formData
          );
        }
      } else {
        const { data } = await api.post("/quick-messages", quickemessageData);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("typeArch", "quickMessage");
          formData.append("file", attachment);
          await api.post(`/quick-messages/${data.id}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("quickMessages.toasts.success"));
      if (typeof reload == "function") {
        reload();
      }
    } catch (err) {
      toastError(err);
    }
    handleClose();
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (quickemessage.mediaPath) {
      await api.delete(`/quick-messages/${quickemessage.id}/media-upload`);
      setQuickemessage((prev) => ({
        ...prev,
        mediaPath: null,
      }));
      toast.success(i18n.t("quickMessages.toasts.deleted"));
      if (typeof reload == "function") {
        reload();
      }
    }
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;

    setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);

    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  const handleEmojiSelect = (emoji, setFieldValue) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + emoji.native.length;

    setFieldValue("message", `${firstHalfText}${emoji.native}${secondHalfText}`);

    setTimeout(() => {
      messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const toggleEmojiPicker = (e) => {
    e.stopPropagation();
    setShowEmojiPicker(!showEmojiPicker);
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("quickMessages.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperComponent={PaperComponent}
        TransitionComponent={Transition}
        aria-labelledby="draggable-dialog-title"
        disableBackdropClick
        disableEscapeKeyDown
        PaperProps={{
          className: classes.paper,
        }}
      >
        <DialogTitle className={classes.dialogTitle} id="draggable-dialog-title">
          <div className={classes.titleContainer}>
            <Avatar className={classes.headerIcon}>
              <MessageIcon />
            </Avatar>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              {quickemessageId
                ? `${i18n.t("quickMessages.dialog.edit")}`
                : `${i18n.t("quickMessages.dialog.add")}`}
            </Typography>
          </div>
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={quickemessage}
          enableReinitialize={true}
          validationSchema={QuickMessageSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickMessage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => (
            <Form>
              <DialogContent dividers className={classes.dialogContent}>
                <Grid container spacing={isXs ? 1 : 3} className={classes.formGridContainer}>
                  {/* First Row - Shortcode */}
                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                      <ShortTextIcon /> {i18n.t("quickMessages.dialog.shortcode")}
                    </Typography>
                    <Field
                      as={TextField}
                      autoFocus
                      name="shortcode"
                      disabled={
                        quickemessageId &&
                        values.visao &&
                        !values.geral &&
                        values.userId !== user.id
                      }
                      error={touched.shortcode && Boolean(errors.shortcode)}
                      helperText={touched.shortcode && errors.shortcode}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.textField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ShortTextIcon className={classes.inputIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Divider className={classes.divider} />

                  {/* Message Type Selector */}
                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                      <LinkIcon /> Tipo de Mensagem
                    </Typography>
                    <FormControl variant="outlined" margin="dense" fullWidth style={{ marginBottom: 16 }}>
                      <Field
                        as={TextField}
                        select
                        label="Tipo de Mensagem"
                        name="messageType"
                        variant="outlined"
                        className={classes.textField}
                        onChange={e => {
                          setFieldValue("messageType", e.target.value);
                          if (e.target.value === "text") {
                            setFieldValue("buttons", []);
                          }
                        }}
                      >
                        <MenuItem value="text">Texto / Mídia (padrão)</MenuItem>
                        <MenuItem value="buttons">Com Botões de Ação (CTA)</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Message Section */}
                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                      <MessageIcon /> {i18n.t("quickMessages.dialog.message")}
                    </Typography>
                    <div className={classes.messageInputContainer}>
                      <Field
                        as={TextField}
                        name="message"
                        inputRef={messageInputRef}
                        error={touched.message && Boolean(errors.message)}
                        helperText={touched.message && errors.message}
                        variant="outlined"
                        margin="dense"
                        disabled={
                          quickemessageId &&
                          values.visao &&
                          !values.geral &&
                          values.userId !== user.id
                        }
                        multiline={true}
                        rows={isMobile ? 4 : 8}
                        fullWidth
                        className={classes.textField}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <div className={classes.emojiPickerContainer}>
                                <IconButton
                                  ref={emojiButtonRef}
                                  className={classes.emojiButton}
                                  onClick={toggleEmojiPicker}
                                  disabled={
                                    quickemessageId &&
                                    values.visao &&
                                    !values.geral &&
                                    values.userId !== user.id
                                  }
                                >
                                  <EmojiIcon />
                                </IconButton>
                                {showEmojiPicker && emojiData && (
                                  <div
                                    ref={emojiPickerRef}
                                    className={classes.emojiPicker}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Picker
                                      data={emojiData}
                                      onEmojiSelect={(emoji) =>
                                        handleEmojiSelect(emoji, setFieldValue)
                                      }
                                      perLine={isXs ? 6 : 8}
                                      emojiSize={20}
                                      skinTonePosition="none"
                                      previewPosition="none"
                                    />
                                  </div>
                                )}
                              </div>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <div className={classes.messageCounter}>
                        {values.message ? values.message.length : 0}/1024
                      </div>
                    </div>
                  </Grid>
                  
                  {/* Variables Section */}
                  <Grid item xs={12}>
                    <Box display="flex" flexWrap="wrap" mt={1} mb={2}>
                      <Typography variant="caption" style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
                        <CodeIcon style={{ fontSize: 16, marginRight: 4 }} /> Variáveis disponíveis:
                      </Typography>
                      <MessageVariablesPicker
                        disabled={
                          isSubmitting ||
                          (quickemessageId &&
                            values.visao &&
                            !values.geral &&
                            values.userId !== user.id)
                        }
                        onClick={(value) => handleClickMsgVar(value, setFieldValue)}
                        buttonClass={classes.variableButton}
                      />
                    </Box>
                  </Grid>
                  
                  {/* Buttons Builder (only when messageType === "buttons") */}
                  {values.messageType === "buttons" && (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                          <LinkIcon /> Botões de Ação (máx. 3)
                        </Typography>
                        <IconButton
                          onClick={() => {
                            if ((values.buttons || []).length < 3) {
                              setFieldValue("buttons", [
                                ...(values.buttons || []),
                                { type: "cta_url", displayText: "", value: "" }
                              ]);
                            }
                          }}
                          disabled={(values.buttons || []).length >= 3}
                          title="Adicionar botão"
                        >
                          <AddCircleIcon color={(values.buttons || []).length >= 3 ? "disabled" : "primary"} />
                        </IconButton>
                      </Box>

                      {(values.buttons || []).length === 0 && (
                        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
                          Clique no "+" para adicionar até 3 botões.
                        </Typography>
                      )}

                      {(values.buttons || []).map((btn, idx) => (
                        <Box
                          key={idx}
                          mb={2}
                          p={2}
                          style={{ border: "1px solid #e0e0e0", borderRadius: 8, background: "#fafafa" }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              Botão {idx + 1}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const updated = (values.buttons || []).filter((_, i) => i !== idx);
                                setFieldValue("buttons", updated);
                              }}
                            >
                              <DeleteOutlineIcon color="error" fontSize="small" />
                            </IconButton>
                          </Box>

                          <TextField
                            select
                            label="Tipo do Botão"
                            value={btn.type}
                            onChange={e => {
                              const updated = [...(values.buttons || [])];
                              updated[idx] = { ...updated[idx], type: e.target.value, value: "" };
                              setFieldValue("buttons", updated);
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            style={{ marginBottom: 8 }}
                          >
                            <MenuItem value="cta_url">
                              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                <LinkIcon fontSize="small" /> Abrir Link (URL)
                              </Box>
                            </MenuItem>
                            <MenuItem value="cta_copy">
                              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                <CopyIcon fontSize="small" /> Copiar Texto
                              </Box>
                            </MenuItem>
                          </TextField>

                          <TextField
                            label="Texto do Botão"
                            placeholder="Ex: Acessar site, Copiar chave PIX..."
                            value={btn.displayText}
                            onChange={e => {
                              const updated = [...(values.buttons || [])];
                              updated[idx] = { ...updated[idx], displayText: e.target.value };
                              setFieldValue("buttons", updated);
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                            style={{ marginBottom: 8 }}
                            inputProps={{ maxLength: 20 }}
                            helperText={`${btn.displayText.length}/20`}
                          />

                          <TextField
                            label={btn.type === "cta_url" ? "URL do Link" : "Texto a Copiar"}
                            placeholder={btn.type === "cta_url" ? "https://..." : "Chave PIX, código, etc..."}
                            value={btn.value}
                            onChange={e => {
                              const updated = [...(values.buttons || [])];
                              updated[idx] = { ...updated[idx], value: e.target.value };
                              setFieldValue("buttons", updated);
                            }}
                            variant="outlined"
                            size="small"
                            fullWidth
                          />
                        </Box>
                      ))}
                    </Grid>
                  )}

                  <Divider className={classes.divider} />

                  {/* Settings Section */}
                  <Grid item xs={12}>
                    <Typography className={classes.sectionTitle}>
                      <VisibilityIcon /> Configurações de Visibilidade
                    </Typography>
                    
                    <div className={classes.twoColumnGrid}>
                      <FormControl variant="outlined" margin="dense" fullWidth>
                        <Field
                          as={TextField}
                          select
                          label={i18n.t("quickMessages.dialog.visao")}
                          name="visao"
                          disabled={
                            quickemessageId &&
                            values.visao &&
                            !values.geral &&
                            values.userId !== user.id
                          }
                          error={touched.visao && Boolean(errors.visao)}
                          value={values.visao ? "true" : "false"}
                          onChange={(e) => {
                            setFieldValue("visao", e.target.value === "true");
                          }}
                          variant="outlined"
                          className={classes.textField}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <VisibilityIcon className={classes.inputIcon} />
                              </InputAdornment>
                            ),
                          }}
                        >
                          <MenuItem value={"true"}>{i18n.t("announcements.active")}</MenuItem>
                          <MenuItem value={"false"}>{i18n.t("announcements.inactive")}</MenuItem>
                        </Field>
                      </FormControl>
                      
                      {values.visao === true && (
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <Field
                            as={TextField}
                            select
                            label={i18n.t("quickMessages.dialog.geral")}
                            name="geral"
                            disabled={
                              quickemessageId &&
                              values.visao &&
                              !values.geral &&
                              values.userId !== user.id
                            }
                            value={values.geral ? "true" : "false"}
                            error={touched.geral && Boolean(errors.geral)}
                            variant="outlined"
                            className={classes.textField}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PublicIcon className={classes.inputIcon} />
                                </InputAdornment>
                              ),
                            }}
                          >
                            <MenuItem value={"true"}>{i18n.t("announcements.active")}</MenuItem>
                            <MenuItem value={"false"}>{i18n.t("announcements.inactive")}</MenuItem>
                          </Field>
                        </FormControl>
                      )}
                    </div>
                  </Grid>
                  
                  {/* Attachment Section with Preview — oculto no modo botões */}
                  {values.messageType !== "buttons" && (quickemessage.mediaPath || attachment) && (
                    <Grid item xs={12}>
                      <Box className={classes.attachmentBox}>
                        {/* Ícone específico por tipo de mídia */}
                        <Avatar className={classes.attachmentIcon}>
                          {quickemessage.mediaType === 'image' && <ImageIcon />}
                          {quickemessage.mediaType === 'video' && <VideoIcon />}
                          {quickemessage.mediaType === 'audio' && <AudioIcon />}
                          {quickemessage.mediaType === 'document' && <DocumentIcon />}
                          {!quickemessage.mediaType && <AttachFileIcon />}
                        </Avatar>
                        <Typography variant="body2" style={{ flex: 1 }}>
                          {attachment ? attachment.name : quickemessage.mediaName}
                        </Typography>
                        <IconButton
                          onClick={() => setConfirmationOpen(true)}
                          disabled={
                            quickemessageId &&
                            values.visao &&
                            !values.geral &&
                            values.userId !== user.id
                          }
                          size="small"
                        >
                          <DeleteOutlineIcon color="secondary" />
                        </IconButton>
                      </Box>
                      
                      {/* Preview específico por tipo de mídia */}
                      {quickemessage.mediaPath && (
                        <Box mt={2} style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                          {quickemessage.mediaType === 'image' && (
                            <img 
                              src={quickemessage.mediaPath} 
                              alt="Preview" 
                              style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', display: 'block' }}
                            />
                          )}
                          
                          {quickemessage.mediaType === 'video' && (
                            <video 
                              controls 
                              style={{ width: '100%', maxHeight: '400px', display: 'block' }}
                            >
                              <source src={quickemessage.mediaPath} />
                              Seu navegador não suporta vídeo.
                            </video>
                          )}
                          
                          {quickemessage.mediaType === 'audio' && (
                            <Box p={2} style={{ background: '#f5f5f5' }}>
                              <audio 
                                controls 
                                style={{ width: '100%' }}
                              >
                                <source src={quickemessage.mediaPath} />
                                Seu navegador não suporta áudio.
                              </audio>
                            </Box>
                          )}
                          
                          {quickemessage.mediaType === 'document' && (
                            <Box p={2} display="flex" alignItems="center" style={{ background: '#f5f5f5' }}>
                              <DocumentIcon style={{ fontSize: 48, marginRight: 16, color: '#666' }} />
                              <Box>
                                <Typography variant="body1" style={{ fontWeight: 600 }}>
                                  {quickemessage.mediaName}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Documento anexado
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <div className={classes.stickyFooter}>
                <DialogActions className={classes.dialogActions}>
                  <Box>
                    {values.messageType !== "buttons" && !attachment && !quickemessage.mediaPath && (
                      <>
                        <input
                          type="file"
                          ref={attachmentFile}
                          onChange={(e) => handleAttachmentFile(e)}
                          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.xml,.zip,.rar"
                          style={{ display: "none" }}
                        />
                        <Button
                          style={{
                            color: "white",
                            backgroundColor: "#437db5",
                            boxShadow: "none",
                            borderRadius: "5px",
                            fontSize: "12px",
                          }}
                          onClick={() => attachmentFile.current.click()}
                          disabled={
                            isSubmitting ||
                            (quickemessageId &&
                              values.visao &&
                              !values.geral &&
                              values.userId !== user.id)
                          }
                          startIcon={<AttachFileIcon />}
                          className={isMobile ? classes.buttonAttach : ''}
                        >
                          {i18n.t("quickMessages.buttons.attach")}
                        </Button>
                        <Button
                          style={{
                            color: "white",
                            backgroundColor: gravando ? "#dc2626" : "#059669",
                            boxShadow: "none",
                            borderRadius: "5px",
                            fontSize: "12px",
                            marginLeft: "8px"
                          }}
                          onClick={gravando ? pararGravacao : iniciarGravacao}
                          disabled={isSubmitting}
                          startIcon={<MicIcon />}
                        >
                          {gravando
                            ? `Parar (${Math.floor(tempoGravado / 60)}:${(tempoGravado % 60)
                                .toString()
                                .padStart(2, "0")})`
                            : "Gravar áudio"}
                        </Button>
                      </>
                    )}
                  </Box>
                  <Box className={classes.actionButtonsContainer}>
                    <Button
                      style={{
                        color: "white",
                        backgroundColor: "#db6565",
                        boxShadow: "none",
                        borderRadius: "5px",
                        fontSize: "12px",
                      }}
                      onClick={handleClose}
                      disabled={isSubmitting}
                      startIcon={<CancelIcon />}
                      className={isMobile ? classes.buttonCancel : ''}
                    >
                      {i18n.t("quickMessages.buttons.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      style={{
                        color: "white",
                        backgroundColor: "#4ec24e",
                        boxShadow: "none",
                        borderRadius: "5px",
                        fontSize: "12px",
                      }}
                      disabled={
                        isSubmitting ||
                        (quickemessageId &&
                          values.visao &&
                          !values.geral &&
                          values.userId !== user.id)
                      }
                      startIcon={<SaveIcon />}
                      className={isMobile ? classes.buttonSave : ''}
                    >
                      {quickemessageId
                        ? `${i18n.t("quickMessages.buttons.edit")}`
                        : `${i18n.t("quickMessages.buttons.add")}`}
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </Button>
                  </Box>
                </DialogActions>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickMessageDialog;